import React from 'react';

export interface LayoutConfig {
    showHeader?: boolean;
    headerTitle?: string;
    headerLeftButton?: 'menu' | 'back' | 'none';
    headerRightButton?: 'alarm' | 'home' | 'none';
    showBottomNavigator?: boolean;
    onHeaderLeftClick?: () => void;
    onHeaderRightClick?: () => void;
}

export interface RouteConfig {
    path: string;
    element: React.LazyExoticComponent<() => JSX.Element>;
    auth: boolean;
    children?: RouteConfig[];
    getLayout?: (params?: any, location?: any) => LayoutConfig;
}

const Home = React.lazy(() => import('../pages/home'));
const Login = React.lazy(() => import('../pages/login'));
const Signup = React.lazy(() => import('../pages/signup'));
const SignupComplete = React.lazy(() => import('../pages/signup/signup-complete'));
const Page404 = React.lazy(() => import('../pages/page404/Page404'));
const Page500 = React.lazy(() => import('../pages/page500/Page500'));
const RepairCase = React.lazy(() => import('../pages/repair-case'));
const RepairCaseDetail = React.lazy(() => import('../pages/repair-case/RepairCaseDetail'));
const SimilarRepairCaseDetail = React.lazy(() => import('../pages/repair-case/SimilarRepairCaseDetail'));
const MyPage = React.lazy(() => import('../pages/mypage'));
const Notifications = React.lazy(() => import('../pages/mypage/notifications'));
const Upload = React.lazy(() => import('../pages/upload'));
const AuthCallback = React.lazy(() => import('../pages/auth/callback'));
const Payment = React.lazy(() => import('../pages/payment/index'));
const PaymentDemo = React.lazy(() => import('../pages/payment/demo'));
const ReturnPayment = React.lazy(() => import('../pages/return/index'));
const Orders = React.lazy(() => import('../pages/mypage/orders'));
const Cancels = React.lazy(() => import('../pages/mypage/cancels'));
const OrderDetail = React.lazy(() => import('../pages/mypage/orders/detail'));
// const RepairCancel = React.lazy(() => import('../pages/mypage/orders/repairCancel'));
// const RepairCancelConfirm = React.lazy(() => import('../pages/mypage/orders/repairCancelconfirm'));
const RepairCancelDetail = React.lazy(() => import('../pages/mypage/cancels/detail'));
const Reviews = React.lazy(() => import('../pages/mypage/reviews'));
const ReviewDetail = React.lazy(() => import('../pages/mypage/reviews/detail'));
const Guide = React.lazy(() => import('../pages/guide'));
const GuideDetail = React.lazy(() => import('../pages/guide/detail'));
// const AboutService = React.lazy(() => import('../pages/mypage/about'));
const AboutDetail = React.lazy(() => import('../pages/mypage/about/detail'));
const Notice = React.lazy(() => import('../pages/mypage/notice'));
const NoticeDetail = React.lazy(() => import('../pages/mypage/notice/detail'));
const MyInfoPage = React.lazy(() => import('../pages/mypage/myInfo'));
const MyActivity = React.lazy(() => import('../pages/mypage/activity'));
const Settings = React.lazy(() => import('../pages/mypage/settings'));
const NotificationSettings = React.lazy(() => import('../pages/mypage/settings/noti'));
const Terms = React.lazy(() => import('../pages/mypage/policy/terms'));
const Privacy = React.lazy(() => import('../pages/mypage/policy/privacy'));
const Refund = React.lazy(() => import('../pages/mypage/policy/refund'));

const routes: RouteConfig[] = [
    // 인증이 필요없는 라우트
    {
        path: '/login',
        element: Login,
        auth: false,
        getLayout: () => ({showHeader: false, showBottomNavigator: false}),
    },
    {
        path: '/signup',
        element: Signup,
        auth: false,
        getLayout: () => ({showHeader: false, showBottomNavigator: false}),
    },
    {
        path: '/signup/complete',
        element: SignupComplete,
        auth: false,
        getLayout: () => ({showHeader: false, showBottomNavigator: false}),
    },
    {
        path: '/404',
        element: Page404,
        auth: false,
        getLayout: () => ({showHeader: false, showBottomNavigator: false}),
    },
    {
        path: '/500',
        element: Page500,
        auth: false,
        getLayout: () => ({showHeader: false, showBottomNavigator: false}),
    },
    {
        path: '/',
        element: Home,
        auth: false,
        getLayout: () => ({}),
    },
    {
        path: '/repair-case',
        element: RepairCase,
        auth: false,
        getLayout: () => ({headerTitle: '수선 사례', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/repair-case/:id',
        element: RepairCaseDetail,
        auth: false,
        getLayout: () => ({headerTitle: '수선 사례 상세', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/repair-case/:id/similar',
        element: SimilarRepairCaseDetail,
        auth: false,
        getLayout: () => ({headerTitle: '유사 수선 사례 상세', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/guide',
        element: Guide,
        auth: false,
        getLayout: () => {
            const queryString = window.location.search;
            const params = new URLSearchParams(queryString);

            // 하단 메뉴바가 아닌 마이페이지에서 이용가이드로 이동 시 뒤로가기 노출
            // queryString을 통해 해당 케이스를 감지합니다

            return params.get('backpage') === 'mypage'
                ? {headerTitle: '이용 가이드', headerLeftButton: 'back', showBottomNavigator: false}
                : {headerTitle: '이용 가이드'};
        },
    },
    {
        path: '/guide/detail',
        element: GuideDetail,
        auth: false,
        getLayout: () => ({headerTitle: '이용 가이드', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/upload',
        element: Upload,
        auth: false,
        getLayout: () => ({}),
    },
    {
        path: '/user/auth/callback/:provider',
        element: AuthCallback,
        auth: false,
        getLayout: () => ({showHeader: false, showBottomNavigator: false}),
    },

    // 인증이 필요한 라우트
    {
        path: '/mypage',
        element: MyPage,
        auth: false,
        getLayout: () => ({headerTitle: '마이페이지'}),
    },
    {
        path: '/mypage/my-info',
        element: MyInfoPage,
        auth: true,
        getLayout: () => ({headerTitle: '마이페이지', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/mypage/settings/notifications',
        element: NotificationSettings,
        auth: true,
        getLayout: () => ({headerTitle: '설정', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/policy/terms',
        element: Terms,
        auth: false,
        getLayout: () => ({headerTitle: '이용약관', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/policy/privacy',
        element: Privacy,
        auth: false,
        getLayout: () => ({headerTitle: '개인정보처리방침', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/policy/refund',
        element: Refund,
        auth: false,
        getLayout: () => ({headerTitle: '환불정책', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/payment',
        element: Payment,
        auth: true,
        getLayout: () => ({
            headerTitle: '결제',
            headerLeftButton: 'back',
            headerRightButton: 'home',
            showBottomNavigator: false,
        }),
    },
    {
        path: '/demo-payment',
        element: PaymentDemo,
        auth: false,
        getLayout: () => ({
            headerTitle: '결제',
            headerLeftButton: 'back',
            headerRightButton: 'home',
            showBottomNavigator: false,
        }),
    },
    {
        path: '/return',
        element: ReturnPayment,
        auth: true,
        getLayout: () => ({
            headerTitle: '반환 결제',
            headerLeftButton: 'back',
            headerRightButton: 'home',
            showBottomNavigator: false,
        }),
    },
    {
        path: '/mypage/orders',
        element: Orders,
        auth: true,
        getLayout: () => ({headerTitle: '주문 목록', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/mypage/cancels',
        element: Cancels,
        auth: true,
        getLayout: () => ({headerTitle: '수선 취소 목록', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/mypage/cancels/detail',
        element: RepairCancelDetail,
        auth: true,
        getLayout: (_params, location) => {
            const searchParams = new URLSearchParams(location.search);
            const from = searchParams.get('from');
            const shouldShowBackButton = from === 'repair-cancel-confirm' ? 'none' : 'back';
            return {headerTitle: '수선 취소 상세', headerLeftButton: shouldShowBackButton, showBottomNavigator: false};
        },
    },
    {
        path: '/mypage/order-detail',
        element: OrderDetail,
        auth: true,
        getLayout: () => ({headerTitle: '주문 상세', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    // CS 환불 처리 전용으로 변경됨에 따라 사용자 환불 신청 라우트 차단
    // {
    //     path: '/mypage/orders/repair-cancel',
    //     element: RepairCancel,
    //     auth: true,
    //     getLayout: () => ({headerTitle: '수선 취소', headerLeftButton: 'back', showBottomNavigator: false}),
    // },
    // {
    //     path: '/mypage/orders/repair-cancel/confirm',
    //     element: RepairCancelConfirm,
    //     auth: true,
    //     getLayout: () => ({headerTitle: '수선 취소', headerLeftButton: 'back', showBottomNavigator: false}),
    // },
    {
        path: '/mypage/reviews',
        element: Reviews,
        auth: true,
        getLayout: () => ({headerTitle: '리뷰 관리', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/mypage/reviews/detail',
        element: ReviewDetail,
        auth: true,
        getLayout: () => ({headerTitle: '리뷰 상세', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/service-intro',
        element: AboutDetail,
        auth: false,
        getLayout: () => ({headerTitle: '서비스 소개'}),
    },
    {
        path: '/mypage/about',
        element: AboutDetail,
        auth: true,
        getLayout: () => ({headerTitle: '서비스 소개', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/mypage/notice',
        element: Notice,
        auth: true,
        getLayout: () => ({headerTitle: '공지사항', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/mypage/notice/detail',
        element: NoticeDetail,
        auth: true,
        getLayout: () => ({headerTitle: '공지사항 상세', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/mypage/my-activity',
        element: MyActivity,
        auth: true,
        getLayout: () => ({headerTitle: '내 활동', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/mypage/settings',
        element: Settings,
        auth: true,
        getLayout: () => ({headerTitle: '설정', headerLeftButton: 'back', showBottomNavigator: false}),
    },
    {
        path: '/mypage/notifications',
        element: Notifications,
        auth: true,
        getLayout: () => ({headerTitle: '알림', headerLeftButton: 'back', showBottomNavigator: false}),
    },
];

export default routes;
